import React, { useCallback, useEffect, useState } from 'react';
import { Grid, IconButton, useMediaQuery } from '@mui/material';
import { GridColDef, GridSortModel } from '@mui/x-data-grid';
import { GridRenderCellParams } from '@mui/x-data-grid-pro';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import { useGetAllReplenishmentReports } from '../../../queries/replenishmentConfig-query';
import { ReplenishmentReportFilters, ReplenishmentReportListResponse } from '../../../@types/replenishmentConfig.type';
import { CustomDataGrid } from '../../../components/layouts/CustomDataGrid';
import { ColumnSelectionItem } from '../../../components/templates/ColumnSelection';
import { HorizontalAlignProps, PageContainer } from '../../../components/atoms/PageContainer';
import { Typography, TypographyVariantProps } from '../../../components/atoms/Typography';
import { InlineGroup } from '../../../components/atoms/InlineGroup';
import { REPLENISHMENT_REPORT_FILTERS } from '../../../constants/replenishmentConfig';
import { Button } from '../../../components/atoms/Button';
import { ReplenishmentFilterFragment } from './ReplenishmentFilterFragment';
import { Chip } from '../../../components/atoms/Chip';
import { ActionPermissions, ColorProps, CustomPermission, ListType, NewReportDataType, OverwriteReportDataType, PropReportDataType, ReportResponse, ScreenSize, SizeProps } from '../../../@types';
import { GridContainer, Wrapper } from '../../../styled/common.styles';
import { PLATFORM_NAME } from '../../../configs/common';
import { TabHandler } from '../../../handlers/TabHandler';
import { GridFeatureModeConstant, IconButtonColor, SavedReportTypeList } from '../../../configs/enums';
import { isUserHasPermission } from '../../../configs/permissions';
import { useGetUserSystemPermissions } from '../../../queries/user-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { generateReportURL } from '../../../utils/report';
import { useAddNewReport, useGetDefaultReportByReportType, useGetReportByCode, useUpdateReportByCode } from '../../../queries/report-query';
import { Snackbar } from '../../../components/atoms/Snackbar';
import { replenishmentColumnSelectionItems } from '../../../constants/replenishmentReport';
import { genarateNewDispatchURLFromReplenishment } from '../../../utils/shipment';

export const ReplenishmentReportList: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const reportCode = searchParams.get('savedReportCode');

  const isMobileView = useMediaQuery(ScreenSize.MOBILE);

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [sortModel, setSortModel] = useState<GridSortModel>();
  const [replenishmentReports, setReplenishmentReports] = useState<ReplenishmentReportListResponse>();
  const [replenishmentFilters, setReplenishmentFilters] = useState<ReplenishmentReportFilters>(REPLENISHMENT_REPORT_FILTERS);
  const [permissions, setPermissions] = useState<CustomPermission[]>();

  const [columns, setColumns] = useState<GridColDef[]>([]);
  const [reportColumnSelectionItems, setReportColumnSelectionItems] = useState<Array<ColumnSelectionItem>>();
  const [reportName, setReportName] = useState('');
  const [savedReportMessage, setSavedReportMessage] = useState('');
  const [reportViewCode, setReportViewCode] = useState<string>();
  const [isDefault, setIsDefault] = useState<boolean>(false);
  const [newReportData, setNewReportData] = useState<PropReportDataType>();
  const [fetchDataGrid, setFetchDataGrid] = useState<boolean>(false);
  const [openToast, setOpenToast] = useState<boolean>(false);
  const [firstLoad, setFirstLoad] = useState<boolean>(false);

  TabHandler(`Replenishment | ${PLATFORM_NAME}`);

  const getUserPermissionsQuery = useGetUserSystemPermissions();
  const useAddNewReportQuery = useAddNewReport();
  const useOverwriteReportQuery = useUpdateReportByCode(reportCode || '');
  const { data: reportQueryData, refetch: refetchGetReportByCodeQuery } = useGetReportByCode(reportCode || '');
  const defaultReportData = useGetDefaultReportByReportType(SavedReportTypeList.ReplenishmentReportList);

  const getAllReplenishmentReports = useGetAllReplenishmentReports(
    {
      currentPage: page, 
      limit: pageSize,
      total: 0
    },
    {
      order_by: sortModel && sortModel?.length > 0 ? sortModel[0].field : '',
      order_direction: sortModel && sortModel?.length > 0 ? (sortModel[0].sort as 'asc' | 'desc') : 'asc'
    },
    replenishmentFilters
  );

  useEffect(() => {
    setFirstLoad(true);
  }, []);

  useEffect(() => {
    !reportQueryData?.dataGridProperties?.columnOrder &&
    setColumns([
      {
        field: 'id',
        headerName: 'ID'
      },
      {
        field: 'clientContract',
        headerName: 'Client/Contract',
        width: 200,
        valueGetter: (_value, row) => row.clientContract
      },
      {
        field: 'location',
        headerName: 'Location',
        width: 200,
        valueGetter: (_value, row) => row.location
      },
      {
        field: 'partTypeOrSkuName', 
        headerName: 'PartType/SkuName', 
        width: 200,
        valueGetter: (_value, row) => row.partTypeOrSkuName
      },
      {
        field: 'min', 
        headerName: 'Min', 
        valueGetter: (_value, row) => row.min
      },
      {
        field: 'max', 
        headerName: 'Max',   
        valueGetter: (_value, row) => row.max
      },
      {
        field: 'includeChildNode', 
        headerName: 'Include Child Node',
        width: 170,
        valueFormatter: (_value, row) => row.includeChildNode ? 'Yes' : 'No'
      },
      {
        field: 'isActive', 
        headerName: 'IsActive', 
        valueFormatter: (_value, row) => row.isActive ? 'Yes' : 'No'
      },
      {
        field: 'averageFreightLeadTimeOneMonth', 
        headerName: 'Average Freight Lead Time One Month',
        width: 300,
        valueGetter: (_value, row) => row.averageFreightLeadTimeOneMonth,
        sortable: false
      },
      {
        field: 'averageFreightLeadTimeThreeMonth', 
        headerName: 'Average Freight Lead Time Three Month',
        width: 300, 
        valueGetter: (_value, row) => row.averageFreightLeadTimeThreeMonth,
        sortable: false
      },
      {
        field: 'averageUsageOneMonth', 
        headerName: 'Average Usage One Month',
        width: 200,
        valueGetter: (_value, row) => row.averageUsageOneMonth,
        sortable: false
      },
      {
        field: 'averageUsageThreeMonth', 
        headerName: 'Average Usage Three Month',
        width: 200,
        valueGetter: (_value, row) => row.averageUsageThreeMonth,
        sortable: false
      },
      {
        field: 'state', 
        headerName: 'State',
        width: 200,
        valueGetter: (_value, row) => row.state,
        sortable: false
      },
      {
        field: 'condition', 
        headerName: 'Condition',
        width: 200,
        valueGetter: (_value, row) => row.condition,
        sortable: false
      },
      {
        field: 'stockOnHand', 
        headerName: 'Stock On Hand',
        width: 150,
        valueGetter: (_value, row) => row.stockOnHand,
        sortable: false
      },
      {
        field: 'inTransit', 
        headerName: 'In Transit',
        width: 150,
        valueGetter: (_value, row) => row.inTransit,
        sortable: false
      },
      {
        field: 'servicesInTrain', 
        headerName: 'Services In Train',
        width: 150, 
        valueGetter: (_value, row) => row.servicesInTrain,
        sortable: false
      },
      {
        field: 'replenishmentQuantity', 
        headerName: 'Replenishment Qty',
        width: 150,
        align: 'center',
        renderCell: (params: GridRenderCellParams) => 
          params.row.replenishmentQuantity.includes('+') ? 
            <Chip label={params.row.replenishmentQuantity} color={ColorProps.Success} size={SizeProps.Medium} /> : 
            <Chip label={params.row.replenishmentQuantity} color={ColorProps.Error} size={SizeProps.Medium} />,
        valueGetter: (_value, row) => row.replenishmentQuantity,
        sortable: false
      },
      {
        field: 'replenishmentRequired', 
        headerName: 'Replenishment Required',
        width: 180,
        align: 'center',
        valueGetter: (_value, row) => row.replenishmentRequired,
        sortable: false
      },
      {
        field: 'distributionCentreMap', 
        headerName: 'Distribution Centre Map',
        width: 300,
        minWidth: 500,
        renderCell: (params: GridRenderCellParams) =>
          <ul className="flex">
            {params.row.distributionCentreMap.map((distributionItem: string, index: number) =>
              <li key={index}>
                {distributionItem}
              </li>
            )}
          </ul>
        ,
        valueGetter: (_value, row) => row.distributionCentreMap,
        sortable: false
      },
      {
        field: 'action', 
        headerName: 'Dispatch',
        headerAlign: 'center',
        align: 'center',
        renderCell: (params: GridRenderCellParams) => ( 
          <IconButton aria-label="dispatch" color={IconButtonColor.Primary} onClick={() => onDispatchClick(params.row)}>
            <LocalShippingIcon />
          </IconButton>
        ),
        sortable: false
      }
    ]);
  }, [permissions, getAllReplenishmentReports.data]);
  
  useEffect(() => {
    getUserPermissionsQuery.data && setPermissions(getUserPermissionsQuery.data);
  }, [getUserPermissionsQuery.data]);

  useEffect(() => {
    (replenishmentReports?.data || reportCode) && getAllReplenishmentReports.refetch();
  }, [page, pageSize, sortModel]);

  useEffect(() => {
    !reportCode && defaultReportData.refetch();
  }, [SavedReportTypeList.ReplenishmentReportList]);
  
  useEffect(() => {
    if (!reportCode && defaultReportData.data && defaultReportData.data?.length > 0) {
      setReplenishmentFilters(defaultReportData.data[0].dataGridProperties?.replenishmentFilters as ReplenishmentReportFilters);
      setSortModel(defaultReportData.data[0].dataGridProperties?.sortModel);
      setReportColumnSelectionItems(defaultReportData.data[0].columnSelection);
      setPageSize(defaultReportData.data[0].dataGridProperties?.pageSize);

      if (defaultReportData.data[0].dataGridProperties?.columnOrder && columns) {
        const updatedColumns = defaultReportData.data[0].dataGridProperties?.columnOrder.map(orderCol => {
          const originalColumn = columns.find(col => col.field === orderCol.field);
          return originalColumn ? { ...originalColumn } : orderCol;
        });

        setColumns(updatedColumns);
      }
    }   
  }, [defaultReportData.data, reportCode]);
  
  useEffect(() => {
    if (reportCode) {
      refetchGetReportByCodeQuery();
      setReportViewCode(reportCode);
    }
  }, [reportCode, fetchDataGrid]);
  
  
  useEffect(() => {
    if (reportCode && reportQueryData && firstLoad) {
      document.title = `${reportQueryData.name} (${reportCode}) | Replenishment | ${PLATFORM_NAME}`;
  
      setReplenishmentFilters(reportQueryData.dataGridProperties?.replenishmentFilters as ReplenishmentReportFilters);
      setSortModel(reportQueryData.dataGridProperties?.sortModel);
      setReportColumnSelectionItems(reportQueryData.columnSelection);
      setPageSize(reportQueryData.dataGridProperties?.pageSize);
      setReportName(reportQueryData.name);
      setIsDefault(reportQueryData.isDefault);

      if (reportQueryData.dataGridProperties?.columnOrder && columns) {
        const updatedColumns = reportQueryData.dataGridProperties?.columnOrder.map(orderCol => {
          const originalColumn = columns.find(col => col.field === orderCol.field);
          return originalColumn ? { ...originalColumn } : orderCol;
        });

        setColumns(updatedColumns);
      }
      
      setFirstLoad(false);
    }   
  }, [reportQueryData, reportViewCode, columns, firstLoad]);
  
  useEffect(() => {
    setNewReportData({
      dataGridProperties: {
        filterModel: { items: [] },
        pageSize: pageSize,
        sortModel: sortModel || [],
        columnOrder: columns,
        replenishmentFilters: replenishmentFilters
      },
      reportTypeName: SavedReportTypeList.ReplenishmentReportList,
      reportName: reportName,
      isDefault: reportQueryData?.isDefault,
      id: reportQueryData?.id,
      reportOwner: reportQueryData?.createdBy,
      columnSelection: reportColumnSelectionItems
    });
  }, [isDefault, reportName, columns, pageSize, replenishmentFilters, sortModel]);
    
  useEffect(() => {
    if(getAllReplenishmentReports.data){
      const allReports = getAllReplenishmentReports.data;
      if(replenishmentFilters.showAllResults){
        setReplenishmentReports(allReports);
      } else {
        const filteredData = allReports.data?.filter(obj => obj.replenishmentRequired.toLowerCase().includes('yes'));
        const filteredResult: ReplenishmentReportListResponse = {
          ... allReports,
          data: filteredData as any,
          total: filteredData?.length || 0
        };
        setReplenishmentReports(filteredResult);
      }
    }
  }, [getAllReplenishmentReports.data, replenishmentFilters]);

  

  const handleColumnsChange = (items: Array<ColumnSelectionItem>) => {
    setReportColumnSelectionItems(items);
  };
    
  const handleSortModelChange = useCallback((sortModel: GridSortModel) => {
    setSortModel(sortModel);
  }, []);
    
  const onSearch = () => {
    getAllReplenishmentReports.refetch();
  };

  const onReset = () => {
    setReplenishmentFilters(REPLENISHMENT_REPORT_FILTERS);
    getAllReplenishmentReports.refetch();
  };

  const savedReport = async (newReportData: NewReportDataType) => {
    setSavedReportMessage('');
    const response: ReportResponse = await useAddNewReportQuery.mutateAsync(newReportData);
    setSavedReportMessage(response?.message ? response?.message : 'Successfully Created');
    const reportURL = generateReportURL(window.location.pathname, response.code);
    setOpenToast(true);
    navigate(reportURL, { replace: true });
  };
  
  const overwriteReport = async (overwriteReportData: OverwriteReportDataType) => {
    setSavedReportMessage('');
    const response: ReportResponse = await useOverwriteReportQuery.mutateAsync(overwriteReportData);
    setSavedReportMessage(response?.message ? response?.message : 'Successfully Updated');
    const reportURL = generateReportURL(window.location.pathname, response.code);
    setOpenToast(true);
    navigate(reportURL, { replace: true });
  };

  const onDispatchClick = (rowData: any) => {
    const dispatchURL = genarateNewDispatchURLFromReplenishment(rowData?.locationId, rowData?.location);
    if (isMobileView) {
      window.open(dispatchURL,'_blank');
    } else {
      navigate(dispatchURL);
    }
  };
    
  return (
    <PageContainer>
      <Wrapper>
        <InlineGroup horizontal={HorizontalAlignProps.Between}>
          <Typography variant={TypographyVariantProps.H5} fontWeight={600}>
            Replenishment
          </Typography>
        </InlineGroup>
        <ReplenishmentFilterFragment replenishmentFilterData={replenishmentFilters} onChange={setReplenishmentFilters}/>
        <InlineGroup horizontal={HorizontalAlignProps.End} childGap={10}>
          <Button onClick={onReset}>Reset</Button>
          <Button onClick={onSearch}>Search</Button>
        </InlineGroup>
        <GridContainer>
          <Grid>
            <Snackbar
              open={openToast}
              autoHideDuration={4000}
              message={savedReportMessage ? savedReportMessage : 'Successfully Created'}
              onClose={() => setOpenToast(false)}
            />
          </Grid>
          <CustomDataGrid
            columns={columns}
            loading={getAllReplenishmentReports.isRefetching || getAllReplenishmentReports.isLoading}
            rows={replenishmentReports?.data || []}
            rowCount={replenishmentReports?.total}
            columnSelectionItems={reportColumnSelectionItems ? reportColumnSelectionItems : replenishmentColumnSelectionItems}
            sortModel={sortModel}
            sortingMode={GridFeatureModeConstant.server}
            onSortModelChange={handleSortModelChange}
            exportFileName={ListType.ReplenishmentReportList}
            enableCSVExport={isUserHasPermission(ActionPermissions.Replenishment_Report_Export, permissions)}
            rowsPerPageOptions={[25, 50, 100]}
            pagination={true}
            page={page}
            pageSize={pageSize}
            paginationMode={GridFeatureModeConstant.server}
            onPageChange={(newPage) => setPage(newPage)}
            onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
            initialState={{
              columns: {
                columnVisibilityModel: {
                  id: false,
                  parentNodeId: false,
                  referenceNumbers: false,
                  status: false,
                },
              },
            }}
            componentsProps={{
              toolbar: {
                printOptions: { disableToolbarButton: true }
              }
            }}
            enableReplenishmentReportFilters={true}
            disableColumnFilter={true}
            replenishmentFilterData={replenishmentFilters}
            handleReplenishmentReportFilters={setReplenishmentFilters}
            getRowHeight={() => 'auto'}
            enableSavedReport
            newReportData={newReportData}
            handleSavedReport={(newReportData: NewReportDataType) => savedReport(newReportData)}
            handleOverwriteReport={(overwriteReportData: OverwriteReportDataType) => overwriteReport(overwriteReportData)}
            reportCode={reportViewCode}
            handleColumnsChange={handleColumnsChange}
            handleRefetch={() => setFetchDataGrid(!fetchDataGrid)}
            setColumns={setColumns}
          />
        </GridContainer>
      </Wrapper>
    </PageContainer>
  );
};